<template>
    <div>
        <v-card class="rounded-lg" elevation="0" color="#41916b">
            <v-card-title class="text-center justify-center" v-if="isEdit">
                <h5 class="font-weight-bold" style="color: white">
                    {{ donorLabel }}  
                </h5>
            </v-card-title> 

            <v-tabs v-model="tab" fixed-tabs background-color="#41916b" dark>
             <v-tabs-slider color="#b1e3cc"></v-tabs-slider>
                <v-tab>{{ title }} {{ typeSerologyTitle }}</v-tab> 

                <v-tab-item>
                    <v-card-text>
                        <v-form ref="form" lazy-validation>
                            <div class="border"> 
                                <p style="font-weight:bold" class="d-flex justify-center">{{ $t('titles.patient_data') }}</p>
                                <v-row>
                                    <v-col class="d-flex" cols="12" sm="3" v-if="!isEdit">
                                        <v-autocomplete  
                                            :rules="formRules" 
                                            :items="donor"
                                            color="#256936"
                                            class="rounded-lg" 
                                            v-model="serologyForm.donor_id" 
                                            item-value="id"
                                            item-text="name" 
                                            dense outlined  
                                            :no-data-text="$t('messages.has_no_patient')"
                                            @change="checkIfHasBloodGroup(serologyForm.donor_id)">
                                            <template v-slot:label>
                                                {{ $t('inputs.serology.potential_donor') }}
                                                <span class="asterisk">*</span>
                                            </template>
                                        </v-autocomplete>
                                    </v-col> 
                                    <v-col class="d-flex" cols="12" sm="3">
                                        <v-text-field 
                                            color="#256936" 
                                            class="rounded-lg" 
                                            :label="$t('inputs.patient_data.cpf')" 
                                            disabled 
                                            outlined
                                            :value="selectedCpf" 
                                            maxlength="14" 
                                            v-mask="'###.###.###-##'"
                                            dense>
                                        </v-text-field>
                                    </v-col>
                                    <v-col class="d-flex" cols="12" sm="3" >
                                        <v-text-field 
                                            color="#256936" 
                                            type="date" 
                                            class="rounded-lg"
                                            :label="$t('inputs.patient_data.birth_date')" 
                                            disabled 
                                            outlined 
                                            :value="selectedBirthDate"
                                            dense>
                                        </v-text-field>
                                    </v-col>  
                                    <v-col class="d-flex" cols="12" sm="3">
                                        <v-text-field 
                                            color="#086318" 
                                            class="rounded-lg" 
                                            :rules="formRules"
                                            :max="maxDate"  
                                            type="date"
                                            @input="onlyDateInput('sample_date', $event)" 
                                            outlined
                                            v-model="serologyForm.sample_date" 
                                            dense>
                                            <template v-slot:label>
                                                {{ $t('inputs.serology.sample_date') }}
                                                <span class="asterisk">*</span>
                                            </template>
                                        </v-text-field>
                                    </v-col>
                                </v-row>
                            </div>

                            <div class="border" v-if="onlySerology && !onlyNat">
                                <p style="font-weight:bold" class="d-flex justify-center">{{ $t('inputs.serology.serology') }}</p>
                                
                                <p style="font-weight:bold">{{ $t('inputs.blood_group.type_report') }}:<span style="color:red">*</span></p>
                                <v-row style="padding-left:10px" class="mb-2">
                                    <v-radio-group :rules="formRules" row v-model="serologyForm.type_report_id">
                                        <v-radio :label="$t('inputs.serology.cornea')" value="1" color="#086318"></v-radio>

                                        <v-radio :label="$t('inputs.serology.esclera')" value="2" color="#086318"></v-radio>

                                        <v-radio :label="$t('inputs.serology.multiple_orgons')" value="3" color="#086318"></v-radio>
                                    </v-radio-group>
                                </v-row> 
                                
                                <v-row dense v-if="!loadingSerologyItems">
                                    <v-col class="d-flex" cols="12" sm="4"
                                        v-for="(serology, index) in defaultSerologyItems" :key="`default-${index}`"
                                        dense>
                                        <v-select  
                                            :items="serology.type_serology_status_id == '1' ? serologyTypeOneItems : serologyTypeTwoItems"
                                            class="rounded-lg" color="#256936" v-model="serology.serology_status_id"
                                            item-value="id" item-text="name" dense outlined
                                            :label="serology.description">
                                        </v-select>
                                    </v-col>
                                    <v-col class="d-flex" cols="12" sm="12">
                                        <v-textarea 
                                            color="#256936" 
                                            rows="2" 
                                            class="rounded-lg" 
                                            v-model="serologyForm.observation" 
                                            outlined 
                                            dense
                                            counter="255"
                                            maxlength="255"
                                        >
                                            <template v-slot:label>
                                                {{ $t('inputs.serology.observations') }}
                                            </template>
                                        </v-textarea>
                                    </v-col>
                                </v-row>

                                <v-row dense v-else class="d-flex justify-center align-center py-4">
                                    <v-progress-circular
                                        indeterminate
                                        color="#41916b"
                                        size="40"
                                    ></v-progress-circular>
                                </v-row>

                                <p style="font-weight:bold">{{ $t('titles.exames_responsibles') }}<span style="color:red">*</span></p>
                                <v-row dense>
                                    <v-col class="d-flex" cols="12" sm="3">
                                        <v-text-field 
                                            color="#256936" 
                                            class="rounded-lg" 
                                            :rules="formRules"
                                            outlined 
                                            v-model="serologyForm.responsible_name"
                                            dense>
                                            <template v-slot:label>
                                                {{ $t('inputs.serology.responsible_name') }}
                                                <span class="asterisk">*</span>
                                            </template>
                                        </v-text-field>
                                    </v-col>

                                    <v-col class="d-flex" cols="12" sm="3">
                                        <v-text-field 
                                            color="#256936" 
                                            class="rounded-lg" 
                                            :rules="formRules"  
                                            outlined 
                                            v-model="serologyForm.advice"
                                            dense>
                                            <template v-slot:label>
                                                {{ $t('inputs.serology.advice') }}
                                                <span class="asterisk">*</span>
                                            </template>
                                        </v-text-field>
                                    </v-col>

                                    <v-col class="d-flex" cols="12" sm="3">
                                        <v-text-field 
                                            color="#256936" 
                                            class="rounded-lg" 
                                            type="datetime-local"
                                            @input="onDateTimeInput('release_date', $event)" 
                                            :max="maxDateTime"
                                            :rules="formRules"  
                                            outlined
                                            v-model="serologyForm.release_date" 
                                            dense>
                                            <template v-slot:label>
                                                {{ $t('inputs.serology.release_date') }}
                                                <span class="asterisk">*</span>
                                            </template>
                                        </v-text-field>
                                    </v-col>
                                        <v-col class="d-flex" cols="12" sm="3">
                                        <v-text-field 
                                            color="#256936" 
                                            class="rounded-lg" 
                                            :rules="formRules" 
                                            outlined 
                                            v-model="serologyForm.report_number"
                                            dense>
                                            <template v-slot:label>
                                                {{ $t('inputs.serology.report_number') }}
                                                <span class="asterisk">*</span>
                                            </template>
                                        </v-text-field>
                                    </v-col> 
                                </v-row> 
                            </div> 
   
                            <div class="border" v-if="onlyNat"> 
                                    <p style="font-weight:bold" class="d-flex justify-center">{{ $t('inputs.serology.nat_serologies') }}</p>

                                    <p style="font-weight:bold">{{ $t('inputs.blood_group.type_report') }}:<span style="color:red">*</span></p>
                                    <v-row style="padding-left:10px" class="mb-2">
                                        <v-radio-group :rules="formRules" row v-model="serologyForm.type_report_id">
                                            <v-radio :label="$t('inputs.serology.cornea')" value="1" color="#086318"></v-radio>

                                            <v-radio :label="$t('inputs.serology.esclera')" value="2" color="#086318"></v-radio>

                                            <v-radio :label="$t('inputs.serology.multiple_orgons')" value="3" color="#086318"></v-radio>
                                        </v-radio-group>
                                    </v-row>

                                    <v-row dense>
                                        <v-col class="d-flex" cols="12" sm="4" v-for="(serology, index) in natSerologyItems"
                                            :key="`nat-${index}`" dense>
                                            <v-select  
                                                :items="serology.type_serology_status_id == '1' ? serologyTypeOneItems : serologyTypeTwoItems"
                                                class="rounded-lg" color="#256936" v-model="serology.serology_status_id"
                                                item-value="id" item-text="name" dense outlined
                                                :label="serology.description">
                                            </v-select>
                                        </v-col>
                                    </v-row>

                                    <v-row>
                                        <v-col class="d-flex" cols="12" sm="12">
                                            <v-textarea 
                                                color="#256936" 
                                                rows="2" 
                                                class="rounded-lg"
                                                v-model="serologyForm.observation" 
                                                outlined 
                                                dense
                                                counter="255"
                                                maxlength="255"
                                            >
                                                <template v-slot:label>
                                                    {{ $t('inputs.serology.observations') }}
                                                </template>
                                            </v-textarea>
                                        </v-col>
                                    </v-row>

                                    <p style="font-weight:bold">{{ $t('titles.exames_responsibles') }}<span style="color:red">*</span></p>
                                    <v-row dense>
                                        <v-col class="d-flex" cols="12" sm="3">
                                            <v-text-field color="#256936" class="rounded-lg" :rules="formRules"
                                                outlined v-model="serologyForm.responsible_name"
                                                dense>
                                                <template v-slot:label>
                                                    {{ $t('inputs.serology.responsible_name') }}
                                                    <span class="asterisk">*</span>
                                                </template>
                                            </v-text-field>
                                        </v-col>

                                        <v-col class="d-flex" cols="12" sm="3">
                                            <v-text-field color="#256936" class="rounded-lg" :rules="formRules"
                                            outlined v-model="serologyForm.advice"
                                                dense>
                                                <template v-slot:label>
                                                    {{ $t('inputs.serology.advice') }}
                                                    <span class="asterisk">*</span>
                                                </template>
                                            </v-text-field>
                                        </v-col>

                                        <v-col class="d-flex" cols="12" sm="3">
                                            <v-text-field color="#256936" class="rounded-lg" type="datetime-local"
                                                @input="onDateTimeInput('release_date', $event)" :max="maxDateTime"
                                                :rules="formRules" outlined
                                                v-model="serologyForm.release_date" dense>
                                                <template v-slot:label>
                                                    {{ $t('inputs.serology.release_date') }}
                                                    <span class="asterisk">*</span>
                                                </template>
                                            </v-text-field>
                                        </v-col>
                                        <v-col class="d-flex" cols="12" sm="3">
                                            <v-text-field 
                                                color="#256936" 
                                                class="rounded-lg" 
                                                :rules="formRules"
                                                outlined v-model="serologyForm.report_number"
                                                dense>
                                                <template v-slot:label>
                                                    {{ $t('inputs.serology.report_number') }}
                                                    <span class="asterisk">*</span>
                                                </template>
                                            </v-text-field>
                                        </v-col> 
                                    </v-row>  
                            </div>

                            <v-row class="buttons pa-3 d-flex justify-center" v-if="!disableForm">
                                <v-btn class="rounded-lg mr-1" elevation="0" @click="createSerology()" color="#41916b">
                                    <span style="color: aliceblue;">{{ buttonDescription }}</span>
                                </v-btn>
                                <p class="space-personalized"></p>
                                <v-btn class="rounded-lg mr-1" elevation="0" color="#96af9c"
                                    :loading="clearLoadingButton" @click="clearForm()">
                                    <span style="color: aliceblue;">{{ $t('buttons.clean_up') }}</span>
                                </v-btn>
                                <p class="space-personalized"></p>
                                <v-btn class="rounded-lg" elevation="0" color="grey"
                                    @click="turnBack()">
                                    <span style="color: aliceblue;">{{ $t('buttons.return') }}</span>
                                </v-btn>
                            </v-row>
                        </v-form>
                    </v-card-text>
                </v-tab-item>
            </v-tabs>
        </v-card>
    </div>
</template>
<script>
import axios from "@/axios";
import { mapActions, mapGetters } from "vuex";
import dateTimeLimit from "@/utils/dateTimeLimit";
import currentUser from "@/utils/currentUser";
import { profileSlug } from "@/helpers/profileSlug";
import Formatters from "@/utils/Formatters";
import {compareStr} from "@/utils/compareStrings";
import { 
    getSerolgyStatus, 
    getSerolgyExams,
    getSerologyDonors,
 } from "@/service/SerologyService"; 
export default {
    data() {
        return {
            tab: 1,
            serologyList: [],
            serologyTypeOneItems: [],
            serologyTypeTwoItems: [],
            serologyForm: {},
            loadingSerologyItems: true,

            donor: [],
            blood_group_id: '',
            formRules: [
                (v) => !!v || "Este campo é obrigatório",
            ],
            disableForm: compareStr(this.profile_slug, 'tec_hemoba_imuno') ? true : false,
            clearLoadingButton: false,
        }
    },

    async created() {
        if (!this.serologyDonor?.type) {
            this.changeSerologyPatientData({});
        }
        await this.getFields();
        if (this.isEdit) {
            await this.fillForm(this.serologyDonor)
        }
    }, 

    computed: {
        ...mapGetters({
            patientDonorId: "getDonorId",
            serologyDonor: "getSorologyPatientData"
        }),

        isEdit() {
            return this.serologyDonor.id
        },

        buttonDescription(){
            return this.isEdit ? this.$t('buttons.to_update') : this.$t('buttons.save')
        },

        title() {
            return this.isEdit ? this.$t('titles.edit_serology') : this.$t('titles.cad_serology')
        },

        typeSerologyTitle(){
            return compareStr(this.serologyType, 'nat') ? 'NAT' : null;
        },


        donorLabel() {
            const { kind_of_donor, name } = this.serologyDonor;

            return `Paciente ${kind_of_donor.name} - ${name}`;
        },

        currentUser() {
            return currentUser.getUserData()
        },

        abilities() {
            return this.currentUser.abilities || []
        },
        maxDateTime() {
            const now = new Date()
            return now.toISOString().slice(0, 16)
        },
        maxDate() {
            const now = new Date()
            return now.toISOString().slice(0, 10)
        },

        profile() {
            return this.currentUser.profile
        },

        profile_slug() {
            return profileSlug(this.profile)
        },

        selectedCpf() {
            if (this.isEdit) return this.serologyDonor.cpf || null

            if (!this.donor || !this.serologyForm.donor_id) return null
            const donor = this.donor.find(x => x.id == this.serologyForm.donor_id)
            return donor ? donor.cpf : null
        },
        selectedBirthDate() {
            if (this.isEdit) return Formatters.dataAtualFormatada(this.serologyDonor.birth_date) || null

            if (!this.donor || !this.serologyForm.donor_id) return null
            const donor = this.donor.find(x => x.id == this.serologyForm.donor_id)
            return donor ? Formatters.dataAtualFormatada(donor.birth_date) : null
        },

        defaultSerologyItems() {
            if(compareStr(this.profile_slug, 'gestorhemoba')) {
                return this.serologyList
            }
            return this.serologyList.filter(item => !this.isNatSerology(item))
        },

        natSerologyItems() {
            return this.serologyList.filter(item => this.isNatSerology(item))
        },

        serologyType() {
            return this.serologyDonor?.type || 'sor';
        },

        onlyNat() {
            return compareStr(this.serologyType, 'nat');
        },

        onlySerology() {
            return compareStr(this.serologyType, 'sor');
        },

        onlyForNatProfile() {
            return [ 'tec_hemoba_nat' ].includes(this.profile_slug)
        },

    },

    methods: {
        ...mapActions([
            "changeSnackBarData",
            "changeSerologyPatientData"
        ]),

        can(ability) {
            return this.abilities.includes(ability)
        },
        isNatSerology(item) {
            return String(item.description || '').toLowerCase().endsWith('_nat')
        },
        onlyDateInput(path, field, value) {
            this[path][field] =
                dateTimeLimit.limiteInputOnlyDate(value)
        },
        onDateTimeInput(field, value) {
            this.serologyForm[field] =
                dateTimeLimit.limitYearField(value)
        },

        async getFields() {
            try {
                this.loadingSerologyItems = true
                this.serologyTypeOneItems =  await getSerolgyStatus('1')
                this.serologyTypeTwoItems = await getSerolgyStatus('2')
                this.serologyList = await getSerolgyExams()
                this.donor = await getSerologyDonors(this.onlyNat ? 'no_nat' : 'no_serology')
            } finally {
                this.loadingSerologyItems = false
            }
        },

        async createSerology() {
            if (!this.$refs.form.validate()) return; 
            this.loading = true;

            try {
                const path = this.onlyForNatProfile || this.onlyNat ? '/nats/' : '/serology/';
                const donor_id =
                    this.serologyDonor?.id ??
                    this.serologyForm.donor_id ??
                    this.patientDonorId ??
                    null; 

                const data = {
                    user_id: this.currentUser.id,
                    donor_id,
                    type_report_id: this.serologyForm.type_report_id, 
                    report_number: this.serologyForm.report_number,
                    sample_date: this.serologyForm.sample_date,
                    release_date: this.formatDateTimeToBackend(this.serologyForm.release_date),
                    responsible_name: this.serologyForm.responsible_name,
                    advice: this.serologyForm.advice,
                    observation: this.serologyForm.observation,
                };
 
                const serologyItens = this.serologyList
                    .filter(item => item.serology_status_id)
                    .map(item => ({
                        id: this.serologyForm.id ? item.serologyItemId : undefined,
                        serology_test_id: item.id,
                        serology_status_id: item.serology_status_id
                    }));

                if (serologyItens.length) {
                    if(this.onlyForNatProfile || this.onlyNat){
                       data.status_nat_tests = serologyItens; 
                    }else{
                        data.status_serology_tests = serologyItens;
                    } 
                }

                const response = await axios({
                    method: this.serologyForm.id ? 'PUT' : 'POST',
                    url: this.serologyForm.id
                        ? `${path}${this.serologyForm.id}`
                        : `${path}create`,
                    data
                });

                this.changeSnackBarData({
                    status: true,
                    type: 'success',
                    titleMessage: response.data.message,
                    bodyMessage: '',
                    cardColor: '#41916b',
                    titleIcon: 'mdi-checkbox-marked-circle'
                });

                setTimeout(() => {
                    this.closeSnackBar();
                    this.onlyNat
                        ? this.$router.push('/sorologia-nat')
                        : this.$router.push('/sorologia')
                }, 3000);

            } catch (err) {

                this.changeSnackBarData({
                    status: true,
                    type: 'error',
                    titleMessage: this.$t('messages.erro_in_serology'),
                    bodyMessage: err.details || '',
                    cardColor: 'red',
                    titleIcon: 'mdi-close-circle'
                });

                setTimeout(() => this.closeSnackBar(), 5000);

            } finally {
                this.loading = false;
            }
        },

        formatDateTimeToBackend(value) {
            if (!value) return null;
            return value.replace("T", " ").slice(0, 16);
        },

        formatDateForInput(dateTime) {
            return dateTime.split(" ")[0];
        },

        checkIfHasBloodGroup(id) {
            axios.get(`blood_groups/list/${id}`).then((response) => {
                this.blood_group_id = response.data[0].id ? response.data[0].id : null
            })
        },

        fillForm() {
            const isNAT = this.onlyForNatProfile || this.onlyNat
            let serolgy = isNAT ? this.serologyDonor.nats[0] : this.serologyDonor.serology[0];

            if (!serolgy) {
                this.$nextTick(() => {
                    this.$set(this.serologyForm, 'donor_id', parseInt(this.serologyDonor.user_id))
                })
            } else {

                this.serologyForm = serolgy
                this.$nextTick(() => {
                    this.serologyForm.donor_id = parseInt(this.serologyDonor.id)
                    this.serologyForm.sample_date = this.formatDateForInput(this.serologyForm.sample_date)
 
                    const existing = isNAT ? serolgy.status_nat_tests : serolgy.status_serology_tests

                    this.serologyList = this.serologyList.map(item => {
                        const found = existing.find(
                            x => x.serology_test.id === item.id
                        )
 
                        return {
                            ...item,
                            serology_status_id: found ? found.serology_status.id : null,
                            serologyItemId: found ? found.id : undefined,
                            type_serology_status_id: found
                                ? parseInt(found.serology_status.type_serology_status_id)
                                : item.type_serology_status_id
                        }
                    })
                })
            }
        },

        clearForm() {
            this.clearLoadingButton = true;
            this.serologyForm = {
                donor_id: null,
                report_number: null,
                sample_date: null,
                responsible_name: null,
                advice: null,
                release_date: null,
                type_report_id: null
            };
            this.serologyList = this.serologyList.map(item => ({
                ...item,
                serology_status_id: null
            }));

            this.blood_group_id = null;

            if (this.$refs.form) {
                this.$refs.form.reset();
                this.$refs.form.resetValidation();
            }

            setTimeout(() => {
                this.clearLoadingButton = false;
            }, 1000);
        },

        closeSnackBar() {
            this.changeSnackBarData({
                status: false,
                type: '',
                titleMessage: '',
                bodyMessage: '',
                cardColor: '',
                titleIcon: '',
            });
        },

        turnBack() {
            this.onlyNat
                ? this.$router.push('/sorologia-nat')
                : this.$router.push('/sorologia')
            this.changeSerologyPatientData({})
        }
    }
}
</script>
<style scoped>
.border {
    border: solid 1px #e1e3e2;
    border-radius: 15px;
    padding: 10px;
    width: 100%; 
    margin-bottom: 5px;
}

.asterisk {
   color: red;
}
</style>
